/**
 * plt API 클라이언트.
 *
 * 이 파일이 지키는 불변식 네 가지입니다.
 *
 *  ① `credentials: "include"` — 세션 쿠키는 `__Host-` host-only + HttpOnly 라 JS 가 읽지 못합니다.
 *     콘솔(`auth.<env>.postdemy.com`)과 API(`api.<env>.postdemy.com`)는 호스트가 다르므로
 *     cross-origin 이지만 같은 registrable domain(`postdemy.com`)이라 **same-site** 입니다 —
 *     그래서 `SameSite=Lax` 쿠키가 실립니다. credentials 를 빼면 모든 호출이 401 이 됩니다.
 *
 *  ② 변경요청(GET/HEAD 외)에는 세션 바인딩 CSRF 토큰을 `X-CSRF-Token` 헤더로 붙입니다 — 서버가
 *     요청의 session id 로 같은 값을 재계산해 상수시간 비교합니다. 토큰이 **비어 있으면 헤더를
 *     생략**합니다(서버가 `AUTH_CSRF_SECRET` 미설정으로 CSRF 를 강등한 상태): 보내도 403 이고,
 *     그 경우의 진짜 원인은 서버 구성이라 여기서 가릴 일이 아닙니다.
 *
 *  ③ CloudKit 봉투를 벗깁니다 — 성공은 `{data: …}`, 에러는 `{error, message}`.
 *
 *  ④ **401 과 그 밖의 실패를 구분합니다.** 401 은 정상적인 "미로그인" 이고 나머지는 오류입니다.
 *     구분하지 않으면 API 장애가 "로그아웃된 것처럼" 보여 사용자가 로그인을 반복하게 됩니다.
 *     구분은 `ApiError.status` 로 하고, 세션 로딩(`loadSession`)이 그 분기를 구현합니다.
 */

import { apiURL } from "./config";
import type {
  ApiKey,
  CreateServiceMemberInput,
  CreateSiteInput,
  IssueApiKeyInput,
  IssuedApiKey,
  Member,
  MemberDetail,
  MemberFilter,
  MemberStatus,
  Role,
  Session,
  Site,
  UpdateSiteInput,
} from "./types";

type HttpMethod = "GET" | "POST" | "PATCH" | "DELETE";

/** 요청 실패를 status/code 와 함께 실어 올립니다 — 호출부가 401 을 따로 처리할 수 있어야 합니다. */
export class ApiError extends Error {
  readonly status: number;
  /** 하우스 에러 코드(`ERR-PLT-xxx` / `ERR-SYS-xxx`). 봉투에 없으면 빈 문자열. */
  readonly code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/** 하우스 에러 봉투 — CloudKit `ErrorResponse{error, message}`. `errorCode` 는 구 표면 호환입니다. */
interface ErrorEnvelope {
  error?: string;
  errorCode?: string;
  message?: string;
}

interface RequestOptions {
  /** JSON 본문. `undefined` 면 Content-Type 도 붙이지 않습니다(빈 본문 DELETE 등). */
  body?: unknown;
  /** 세션의 `csrfToken`. 변경요청에서만 의미가 있고, 빈 문자열이면 헤더를 생략합니다. */
  csrfToken?: string;
}

/** 파싱된 본문을 에러 봉투로 봅니다 — 객체가 아니면(HTML 에러 페이지 등) 봉투가 없는 것으로 취급합니다. */
const asEnvelope = (payload: unknown): ErrorEnvelope | null =>
  payload !== null && typeof payload === "object" ? payload : null;

/** 봉투에서 사용자에게 보일 메시지를 만듭니다(코드가 있으면 괄호로 덧붙여 원인 추적을 돕습니다). */
function errorMessage(payload: ErrorEnvelope | null, status: number): string {
  const code = payload?.error ?? payload?.errorCode ?? "";
  const msg = payload?.message ?? "";
  if (msg && code) return `${msg} (${code})`;
  if (msg) return msg;
  if (status === 401) return "로그인이 필요합니다.";
  if (status === 403) return "이 작업에 필요한 권한이 없습니다.";
  if (status === 404) return "대상을 찾을 수 없습니다.";
  return `요청이 실패했습니다 (HTTP ${status}).`;
}

/**
 * request 는 세션 쿠키를 실어 API 를 호출하고 하우스 응답 봉투를 벗깁니다.
 *
 * 반환 타입이 `T | null` 인 이유는 204(본문 없음)와 JSON 파싱 실패를 같은 자리에서 흡수하기
 * 때문입니다 — 호출부가 `?? []` 로 빈 목록을 만들 수 있게 합니다.
 */
export async function request<T>(
  method: HttpMethod,
  path: string,
  { body, csrfToken }: RequestOptions = {},
): Promise<T | null> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  // 안전 메서드에는 CSRF 토큰을 붙이지 않습니다(서버 게이트가 면제하는 메서드입니다).
  if (method !== "GET" && csrfToken) headers["X-CSRF-Token"] = csrfToken;

  const res = await fetch(apiURL(path), {
    method,
    headers,
    credentials: "include", // 세션 쿠키(same-site, Lax)
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 204) return null;

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const envelope = asEnvelope(payload);
    throw new ApiError(
      errorMessage(envelope, res.status),
      res.status,
      envelope?.error ?? envelope?.errorCode ?? "",
    );
  }

  // CloudKit 성공 봉투는 data 안에 본문을 담습니다. 봉투가 없는 응답도 그대로 받습니다.
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T | null;
}

// ── 세션 ────────────────────────────────────────────────────

/**
 * loadSession 은 로그인 상태를 판정합니다.
 *
 * **401 은 정상적인 "미로그인" 이며 예외가 아닙니다** — `null` 을 돌려줍니다. 네트워크/서버 오류는
 * 그대로 던져 호출부가 "미로그인" 과 구분할 수 있게 합니다(위 불변식 ④).
 */
export async function loadSession(): Promise<Session | null> {
  try {
    const data = await request<Session>("GET", "/auth/session");
    return data?.authenticated ? data : null;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export const logout = (csrfToken: string): Promise<unknown> =>
  request("POST", "/auth/logout", { csrfToken });

// ── 서비스 카탈로그 ────────────────────────────────────────

export const fetchSites = async (): Promise<Site[]> =>
  (await request<Site[]>("GET", "/api/v1/sites")) ?? [];

export const createSite = (input: CreateSiteInput, csrfToken: string): Promise<Site | null> =>
  request<Site>("POST", "/api/v1/sites", { body: input, csrfToken });

export const updateSite = (
  siteId: number,
  input: UpdateSiteInput,
  csrfToken: string,
): Promise<Site | null> =>
  request<Site>("PATCH", `/api/v1/sites/${siteId}`, { body: input, csrfToken });

export const deleteSite = (siteId: number, csrfToken: string): Promise<unknown> =>
  request("DELETE", `/api/v1/sites/${siteId}`, { csrfToken });

// ── 구성원 / 역할 ──────────────────────────────────────────

/** 빈 필터 값은 쿼리에서 생략합니다 — 서버가 빈 문자열을 "전체" 로 해석하긴 하지만 URL 을 짧게 둡니다. */
export const fetchMembers = async (filter: MemberFilter): Promise<Member[]> => {
  const q = new URLSearchParams();
  if (filter.type) q.set("type", filter.type);
  if (filter.status) q.set("status", filter.status);
  if (filter.q) q.set("q", filter.q);
  const suffix = q.toString() ? `?${q.toString()}` : "";
  return (await request<Member[]>("GET", `/api/v1/members${suffix}`)) ?? [];
};

export const createServiceMember = (
  input: CreateServiceMemberInput,
  csrfToken: string,
): Promise<Member | null> => request<Member>("POST", "/api/v1/members", { body: input, csrfToken });

export const fetchMemberDetail = (memberId: number): Promise<MemberDetail | null> =>
  request<MemberDetail>("GET", `/api/v1/members/${memberId}`);

export const setMemberStatus = (
  memberId: number,
  status: MemberStatus,
  csrfToken: string,
): Promise<Member | null> =>
  request<Member>("PATCH", `/api/v1/members/${memberId}`, { body: { status }, csrfToken });

export const grantRole = (
  memberId: number,
  roleId: number,
  csrfToken: string,
): Promise<unknown> =>
  request("POST", `/api/v1/members/${memberId}/roles`, { body: { roleId }, csrfToken });

export const revokeRole = (
  memberId: number,
  roleId: number,
  csrfToken: string,
): Promise<unknown> =>
  request("DELETE", `/api/v1/members/${memberId}/roles/${roleId}`, { csrfToken });

export const fetchRoles = async (): Promise<Role[]> =>
  (await request<Role[]>("GET", "/api/v1/roles")) ?? [];

// ── API 키 ─────────────────────────────────────────────────

/** memberId 를 빼면 서버가 **호출자 자신**의 키를 돌려주므로 항상 명시합니다. */
export const fetchApiKeys = async (memberId: number): Promise<ApiKey[]> =>
  (await request<ApiKey[]>("GET", `/api/v1/api-keys?memberId=${String(memberId)}`)) ?? [];

export const issueApiKey = (input: IssueApiKeyInput, csrfToken: string): Promise<IssuedApiKey | null> =>
  request<IssuedApiKey>("POST", "/api/v1/api-keys", { body: input, csrfToken });

/** 교체 — 새 키를 받고 구 키는 회수 전까지 함께 유효합니다(overlap). */
export const rotateApiKey = (keyId: string, csrfToken: string): Promise<IssuedApiKey | null> =>
  request<IssuedApiKey>("POST", `/api/v1/api-keys/${encodeURIComponent(keyId)}/rotate`, { csrfToken });

export const revokeApiKey = (keyId: string, reason: string, csrfToken: string): Promise<unknown> =>
  request("DELETE", `/api/v1/api-keys/${encodeURIComponent(keyId)}`, {
    body: reason ? { reason } : {},
    csrfToken,
  });
